import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'

import { NodeUI } from 'modules/workflows/components/Node/types'
import { VariablesConfig } from 'modules/workflows/components/Sidepanel/VariableSelectorTypes'
import { createConditionalFormSchema } from 'modules/workflows/components/Sidepanel/schemas/conditionalFormSchema'
import {
  FormContext,
  NodeSidepanelSchema,
  createNodeSidepanelSchema,
} from 'modules/workflows/components/Sidepanel/schemas/nodeSidepanelSchema'
import { useWorkflowContext } from 'modules/workflows/contexts/WorkflowContext'

interface Props {
  selectedNode: NodeUI
  variablesConfig: VariablesConfig
}
export const SidePanelFormProvider = ({
  selectedNode,
  variablesConfig,
  children,
}: React.PropsWithChildren<Props>) => {
  const { setSidepanelDirty, handlersConfig, triggerConfig } = useWorkflowContext()

  const initialHandlerName = String(selectedNode.data.handler_name)

  const getFormValues = () => {
    const handlerForm = handlersConfig[initialHandlerName]

    if (selectedNode.type === 'task' || selectedNode.type === 'approvals') {
      return {
        nodeType: selectedNode.type,
        title: selectedNode.data.title,
        description: selectedNode.data.description,
        nodeData: {
          payload: handlerForm?.initializer?.(selectedNode.data.payload),
          handlerType: initialHandlerName,
        },
      }
    }

    if (selectedNode.type === 'condition') {
      return {
        nodeType: selectedNode.type,
        title: selectedNode.data.title,
        description: selectedNode.data.description,
        comparisons: selectedNode.data.comparisons || [],
      }
    }

    if (selectedNode.type === 'trigger') {
      return {
        nodeType: selectedNode.type,
        title: selectedNode.data.title || triggerConfig.title || '',
        description: selectedNode.data.description || triggerConfig.description || '',
        nodeData: {},
      }
    }

    return {
      //other types(conditionIf, conditionElse) are not supported
      nodeType: selectedNode.type,
      title: selectedNode.data.title,
      description: selectedNode.data.description,
      nodeData: {},
    }
  }
  const [context] = useState<FormContext>({
    variablesConfig,
  })

  const form = useForm<NodeSidepanelSchema>({
    mode: 'all',
    context,
    resolver: async (values, formContext: FormContext, options) => {
      // Handle conditional form schema separately
      if (values.nodeType === 'condition') {
        const createResolver = zodResolver(createConditionalFormSchema(formContext))
        return createResolver(values, formContext, options)
      }

      const handlerName = values.nodeData.handlerType
      const handlerForm = handlersConfig[handlerName]
      const handlerSchema =
        handlerForm?.schema(formContext) ?? createNodeSidepanelSchema(formContext) //use generic schema by default, which shows error message when handler type is not selected

      const createResolver = zodResolver(handlerSchema)

      return createResolver(values, context, options)
    },
    defaultValues: getFormValues(),
  })

  const { isDirty } = form.formState

  useUnsafeEffect(() => {
    setSidepanelDirty(isDirty)
  }, [isDirty, setSidepanelDirty])

  return <FormProvider {...form}>{children}</FormProvider>
}
